import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ReaderWithLocationID, PaymentsTerminalController } from '@frontend/payments-terminal-controller';

type DiscoverReadersArgs = Parameters<typeof PaymentsTerminalController.discoverReaders>[number][number];

const generateDiscoverReadersQueryKey = (locationId: string | undefined) => [
  'PaymentsTerminalController.discoverReaders',
  locationId,
];

export const useDiscoverReaderQuery = ({ locationId, stripeLocationId }: Partial<DiscoverReadersArgs>) => {
  const readerData = useMemo(() => {
    if (!locationId) return;

    return {
      locationId,
      stripeLocationId: stripeLocationId || '',
    };
  }, [locationId, stripeLocationId]);

  return useQuery({
    queryKey: generateDiscoverReadersQueryKey(locationId),
    queryFn: () => {
      if (!readerData) {
        throw new Error('No reader location provided');
      }
      return PaymentsTerminalController.discoverReaders([readerData]);
    },
    staleTime: 5e3, // 5 seconds
    cacheTime: 60 * 60e3, // 60 minutes
    refetchOnWindowFocus: true,
    enabled: !!readerData,
  });
};

export const useOptimisticResetDiscoverReaderQuery = (locationId: string | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = generateDiscoverReadersQueryKey(locationId);

  const optimisticRemoveReaderStatus = useCallback(
    (readerId?: string) => {
      if (!readerId) return false;

      queryClient.setQueryData(queryKey, (oldData: ReaderWithLocationID[] | undefined) => {
        const currentReaders = oldData || [];
        return currentReaders.map((reader) => {
          if (reader?.id !== readerId) {
            return reader;
          }
          return {
            ...reader,
            action: {
              failureCode: '',
              failureMessage: '',
              type: '',
              status: '',
              ProcessPaymentIntent: {
                id: null,
              },
            },
          };
        });
      });

      return true;
    },
    [queryKey, queryClient]
  );

  const optimisticAddReaderWaitingInputStatus = useCallback(
    (readerId?: string) => {
      queryClient.setQueryData(queryKey, (oldData: ReaderWithLocationID[] | undefined) => {
        const currentReaders = oldData || [];
        return currentReaders.map((reader) => {
          if (reader?.id !== readerId) {
            return reader;
          }
          return {
            ...reader,
            action: {
              failureCode: '',
              failureMessage: '',
              type: 'optimistic_process_payment_intent',
              status: 'in_progress',
              ProcessPaymentIntent: {
                id: null,
              },
            },
          };
        });
      });
    },
    [queryKey, queryClient]
  );

  return { optimisticRemoveReaderStatus, optimisticAddReaderWaitingInputStatus };
};
export const useInvalidateDiscoverReaderQuery = (locationId: string | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = generateDiscoverReadersQueryKey(locationId);
  const invalidateQuery = () => queryClient.invalidateQueries(queryKey);
  return { invalidateQuery };
};
